/**
 * ONE rule for keeping an agent inside the workspace the user is chatting in.
 *
 * 🔴 THE LEAK THIS CLOSES WAS PROVEN, NOT THEORETICAL.
 * `AdkSerializableContext.brandKitId` has always been resolved once per request
 * by `resolveActiveBrandKit` (`server/api/agent/index.post.ts`) and its own
 * doc comment says it exists so "a sibling workspace's catalog never leaks into
 * this one's tool results". Two of the tools that read `social_posts` —
 * `find-posts` and `get-top-posts` — never referenced it. They filtered on
 * `user_id` alone, which is correct for IDENTITY (`social_posts` is
 * account-scoped in `table-scope.generated.ts`) and is NOT sufficient for
 * WORKSPACE, because the same table carries its own `brand_kit_id`.
 *
 * Measured on the real account 2026-09-08: one owner, FOUR workspaces
 * (Mora AI Studio, Pulse Endurance Sports, ZZ Repro Test, ITC Tax), 447 posts.
 * Chatting on the ITC Tax workspace, those two tools returned the other three
 * workspaces' posts as grounding — indistinguishable from ITC's own.
 *
 * It escalated on the write side. `rewrite-content`, `schedule-content` and
 * `publish-now` resolve their target with `.eq('id', post_id).eq('user_id', …)`
 * and no workspace check at all, so a post surfaced by the leaky reads could be
 * rewritten, rescheduled or **published live** from the wrong client's chat.
 * That is the unrecoverable one: it puts one client's content on another
 * client's channel.
 *
 * ---------------------------------------------------------------------------
 * WHY READS TOLERATE NULL AND WRITES DO NOT
 * ---------------------------------------------------------------------------
 * 1,908 of 2,141 `social_posts` rows carry `brand_kit_id IS NULL` — the column
 * was added pre-launch and deliberately never backfilled ("No backfill --
 * pre-launch, no real users", 20260704153448). So a strict `.eq` on reads would
 * not fix a leak, it would blank the agent: it would hide 89% of the corpus and
 * the model would answer "you have no posts" to someone with hundreds.
 *
 * The honest line is therefore **never cross a KNOWN boundary**:
 *   - A row stamped with workspace A must never surface or be mutated in B.
 *   - A row stamped with nothing belongs to no workspace and stays visible,
 *     because excluding it invents an absence that is not true either.
 * That is strictly better than today on both axes, and it degrades correctly as
 * the backfill lands: every row that gains a workspace leaves the shared pool
 * automatically, with no code change here.
 *
 * `isRowInWorkspace` is the write-side half and is deliberately stricter in one
 * respect only — it is the caller's job to refuse, and refusing is cheap
 * compared to publishing the wrong client's post.
 */

/** The `.or()` filter string, or null when no workspace is in scope. */
export function workspaceOrFilter(brandKitId: string | null | undefined): string | null {
  if (!brandKitId) return null;
  return `brand_kit_id.eq.${brandKitId},brand_kit_id.is.null`;
}

/**
 * Narrow a PostgREST query to the active workspace plus unattributed rows.
 *
 * A `null`/`undefined` `brandKitId` means the account has no active workspace
 * (or this call site has not wired one) and the query is returned untouched —
 * the pre-existing behaviour, never a silent empty result.
 */
export function scopeQueryToWorkspace<T extends { or(filter: string): T }>(
  query: T,
  brandKitId: string | null | undefined,
): T {
  const filter = workspaceOrFilter(brandKitId);
  return filter ? query.or(filter) : query;
}

/**
 * May a row owned by `rowBrandKitId` be MUTATED from `activeBrandKitId`?
 *
 * False only when both sides are known and they differ. An unattributed row is
 * allowed through: it belongs to no workspace, and blocking it would break
 * every legacy post while protecting nothing.
 */
export function isRowInWorkspace(
  rowBrandKitId: string | null | undefined,
  activeBrandKitId: string | null | undefined,
): boolean {
  if (!activeBrandKitId) return true;
  if (!rowBrandKitId) return true;
  return rowBrandKitId === activeBrandKitId;
}

/**
 * The refusal a tool returns when a write would cross a workspace boundary.
 *
 * Names the boundary without naming the other workspace — the other client's
 * name is not this chat's to know, which is the same reason the desk payload
 * has a forbidden-field list.
 */
export const CROSS_WORKSPACE_REFUSAL =
  'That item belongs to a different workspace. Switch to that workspace to work on it.';
